import React from 'react';
import {
  Skeleton,
  AlertTriangleIcon,
  Button,
  ChevronLeftIcon,
  ChevronRightIcon,
  Edit,
} from '@pagespeedify/ui';
import { Task } from '../../types';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../../context/SidebarContext';
import { useAuth } from '../../lib/auth/AuthContext';

interface TaskListProps {
  tasks: Task[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  selectedTask?: Task | null;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  usage?: { count: number; limit: number }; // Added usage prop
  onTaskClick?: (task: Task) => void; // Optional custom click handler
}

const TaskList: React.FC<TaskListProps> = ({
  tasks,
  isLoading,
  isError,
  error,
  selectedTask,
  currentPage,
  totalPages,
  setCurrentPage,
  usage, // Added usage prop
  onTaskClick, // Optional custom click handler
}) => {
  const navigate = useNavigate();
  const { setIsSidebarOpen } = useSidebar();
  const { user } = useAuth();

  const handleSelectTask = (task: Task) => {
    // Use custom handler if provided
    if (onTaskClick) {
      onTaskClick(task);
      return;
    }

    // Default navigation based on task type
    if (task.type === 'agent') {
      navigate(`/agent?taskId=${task.id}`);
    } else if (task.type === 'monitoring') {
      navigate(`/monitoring`, { state: { selectedTask: task } });
    } else if (task.type === 'image_optimization') {
      navigate(`/image-optimization`, { state: { taskId: task.id } });
    } else {
      navigate(`/tasks/${task.id}`);
    }
    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  };

  const handleEditTask = (task: Task) => {
    if (task.type === 'monitoring') {
      navigate(`/monitoring`, { state: { editingTask: task } });
    } else if (task.type === 'image_optimization') {
      navigate(`/image-optimization`, { state: { taskId: task.id } });
    } else {
      navigate(`/tasks/${task.id}`);
    }
    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  };

  // Use the backend's usage count if available, otherwise fall back to displayed tasks length
  const displayedUsageCount = usage?.consumedInPeriod ?? usage?.count ?? tasks.length;

  return (
    <>
      <div className="flex-1 min-h-0 space-y-4">
        {isLoading && (
          <div className="space-y-1">
            <Skeleton className="h-10 w-full rounded-md" />
            <Skeleton className="h-10 w-full rounded-md" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>
        )}
        {isError && (
          <div className="text-red-600 p-2 bg-red-50 rounded-md mx-1">
            <AlertTriangleIcon className="inline h-4 w-4 mr-1" />
            <span className="text-xs">Error fetching tasks: {error?.message || 'Unknown error'}</span>
          </div>
        )}
        {!isLoading && !isError && tasks.length === 0 && (
          <div className="text-muted-foreground p-2 text-center text-xs">
            No tasks match the current filters.
          </div>
        )}
        {!isLoading && !isError && (
          <div className="flex items-center justify-between w-full mt-2 mb-4">
            <span className="text-sm text-muted-foreground">
              {displayedUsageCount} / {usage?.limit || 'Loading...'}
            </span>
          </div>
        )}
        {!isLoading && !isError && tasks.map((task, index) => {
          const statusColors = {
            open: 'border-l-blue-500',
            in_progress: 'border-l-yellow-500',
            resolved: 'border-l-green-500',
            closed: 'border-l-gray-500',
            failed: 'border-l-red-500',
            deleting: 'border-l-indigo-500',
          };
          const statusColor = statusColors[task.status as keyof typeof statusColors] || 'border-l-gray-400';

          const priorityColors = {
            high: 'bg-red-500',
            medium: 'bg-orange-500',
            low: 'bg-blue-500',
          };
          const priority = typeof task.priority === 'string' ? task.priority : 'medium';
          const priorityColor = priorityColors[priority as keyof typeof priorityColors] || 'bg-gray-400';

          // Check if current user is task creator (for AI access indicators)
          const isTaskCreator = user?.email && task.created_by?.email === user.email;

          return (
            <div
              key={task.id}
              className={`border-l-2 ${statusColor} hover:bg-muted/50 cursor-pointer transition-colors relative group pl-2 rounded-r-md ${selectedTask?.id === task.id ? 'bg-muted' : ''}`}
              onClick={() => handleSelectTask(task)}
              data-tour={index === 0 ? "task-item" : undefined}
              data-task-status={index === 0 ? "task-status" : undefined}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full ${priorityColor} flex-shrink-0`} title={priority} data-tour="task-priority" />
                    <h3 className="truncate">{task.title || 'Untitled Task'}</h3>
                    {!isTaskCreator && task.created_by?.email && (
                      <span
                        className="text-xs text-muted-foreground opacity-60"
                        title="AI features restricted to task creator"
                      >
                        🚫
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity ml-1 flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditTask(task);
                  }}
                  title="Edit task"
                >
                  <Edit className="h-2.5 w-2.5" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-center p-3 flex-shrink-0">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} title="Previous page">
            <ChevronLeftIcon className="h-4 w-4" />
          </Button>
          <span className="mx-3 text-xs text-muted-foreground">{currentPage} / {totalPages}</span>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} title="Next page">
            <ChevronRightIcon className="h-4 w-4" />
          </Button>
        </div>
      )}
    </>
  );
};

export default TaskList;